import Button from "@mui/material/Button";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import BatteryRunawayAlarm from "./components/BatteryRunawayAlarm";
import { DiagnosticMonitorGrid } from "./components/DiagnosticMonitorGrid";
import FilterToolbar from "./components/FilterToolbar";
import useAlertEvaluation from "./hooks/useAlertEvaluation";
import { useBatteryRunawayAlarm } from "./hooks/useBatteryRunawayAlarm";
import { useDemoData } from "./hooks/useDemoData";
import { useDiagnosticMonitorData } from "./hooks/useDiagnosticMonitorData";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { primeAudioContext } from "@/utilities/alertSound";
import isDemo from "@/utilities/isDemo";
import { mapLazy } from "@/utilities/mapLazy";

const FilterStackLayout = mapLazy(
  "FilterStackLayout",
  import("@/components/layout/FilterStackLayout"),
);

interface PageContentProps {
  /** The page renders either the live hook or the demo hook; it reads only the fields they share. */
  data: ReturnType<typeof useDiagnosticMonitorData> | ReturnType<typeof useDemoData>;
}

function PageContent({ data: hookData }: PageContentProps) {
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const savedFilterRef = useRef<GridFilterModel | null>(null);
  const {
    data,
    total,
    drivingDevices,
    chargingDevices,
    chargeInterruptDevices,
    notChargingDevices,
    withPMDevices,
    withStatusDevices,
  } = hookData;

  const {
    alertRules = [],
    apiRef,
    diagnosticUnits,
    precisions,
    alertDisplayMode,
    alertAutoDismiss,
  } = useDataGridSettings();
  const { violatedDevices, violationsByRule } = useAlertEvaluation(
    data,
    alertRules,
    apiRef,
    diagnosticUnits,
    precisions,
    alertDisplayMode,
    alertAutoDismiss,
    savedFilterRef,
  );

  const { t } = useTranslation();
  const { phase, muted, deviceName, alarmDeviceId, mute, goToDevice, acknowledge, triggerTest } =
    useBatteryRunawayAlarm(data, apiRef);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(700));

  // Unlock audio on the user's first interaction so the event-driven Battery Runaway siren
  // (which fires from a refetch, not a gesture) isn't blocked by browser autoplay policy.
  useEffect(() => {
    primeAudioContext();
  }, []);

  return (
    <>
      <FilterStackLayout
        isMobile={isMobile}
        minHeight={60}
        filters={
          <FilterToolbar
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            deviceStats={{
              drivingDevices,
              chargingDevices,
              chargeInterruptDevices,
              notChargingDevices,
              withStatusDevices,
              withPMDevices,
            }}
            violationsByRule={violationsByRule}
            violatedDevices={violatedDevices}
            data={data}
            savedFilterRef={savedFilterRef}
          />
        }
        content={
          <DiagnosticMonitorGrid
            data={data}
            deviceCount={total}
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            violatedDevices={violatedDevices}
            alarmDeviceId={alarmDeviceId}
            savedFilterRef={savedFilterRef}
          />
        }
      />

      {import.meta.env.DEV && (
        <Button
          variant="contained"
          color="error"
          onClick={triggerTest}
          sx={{ position: "fixed", bottom: 16, left: 16, zIndex: 1300 }}
        >
          {t("batteryRunaway.testButton")}
        </Button>
      )}

      <BatteryRunawayAlarm
        phase={phase}
        muted={muted}
        deviceName={deviceName}
        onMute={mute}
        onGoToDevice={goToDevice}
        onAcknowledge={acknowledge}
      />
    </>
  );
}

function RealDiagnosticMonitorPage() {
  const data = useDiagnosticMonitorData();
  return <PageContent data={data} />;
}

function DemoDiagnosticMonitorPage() {
  const data = useDemoData();
  return <PageContent data={data} />;
}

export default function DiagnosticMonitorPage() {
  return isDemo ? <DemoDiagnosticMonitorPage /> : <RealDiagnosticMonitorPage />;
}
